// Парсинг HTML в Tiptap nodes (блочные элементы)
const htmlToTiptapNodes = (html: string): any[] => {
  if (!html || !html.trim()) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const parseInlineNode = (
    domNode: ChildNode,
    inheritedMarks: any[] = []
  ): any[] => {
    const result: any[] = [];

    if (domNode.nodeType === Node.TEXT_NODE) {
      const text = domNode.textContent || '';
      if (text) {
        result.push(
          inheritedMarks.length > 0
            ? { type: 'text', text, marks: inheritedMarks }
            : { type: 'text', text }
        );
      }
    } else if (domNode.nodeType === Node.ELEMENT_NODE) {
      const element = domNode as Element;

      if (element.tagName === 'BR') {
        result.push({ type: 'hardBreak' });
      } else {
        const marks: any[] = [...inheritedMarks];

        // Определяем marks по тегам
        if (element.tagName === 'STRONG' || element.tagName === 'B') {
          marks.push({ type: 'bold' });
        }
        if (element.tagName === 'EM' || element.tagName === 'I') {
          marks.push({ type: 'italic' });
        }
        if (element.tagName === 'S' || element.tagName === 'STRIKE') {
          marks.push({ type: 'strike' });
        }
        if (
          element.tagName === 'CODE' &&
          element.parentElement?.tagName !== 'PRE'
        ) {
          marks.push({ type: 'code' });
        }
        if (element.tagName === 'MARK') {
          marks.push({ type: 'highlight' });
        }
        if (element.tagName === 'A') {
          marks.push({
            type: 'link',
            attrs: {
              href: element.getAttribute('href') || '#',
              target: element.getAttribute('target') || '_blank',
            },
          });
        }

        // Рекурсивно обрабатываем дочерние элементы
        Array.from(element.childNodes).forEach((child) => {
          result.push(...parseInlineNode(child, marks));
        });
      }
    }

    return result;
  };

  const parseBlockNode = (element: Element): any => {
    switch (element.tagName) {
      case 'P':
        return {
          type: 'paragraph',
          content: parseInlineNode(element),
        };

      case 'UL':
        return {
          type: 'bulletList',
          content: Array.from(element.children)
            .filter((child) => child.tagName === 'LI')
            .map((li) => ({
              type: 'listItem',
              content: [
                {
                  type: 'paragraph',
                  content: parseInlineNode(li),
                },
              ],
            })),
        };

      case 'OL':
        return {
          type: 'orderedList',
          content: Array.from(element.children)
            .filter((child) => child.tagName === 'LI')
            .map((li) => ({
              type: 'listItem',
              content: [
                {
                  type: 'paragraph',
                  content: parseInlineNode(li),
                },
              ],
            })),
        };

      case 'BLOCKQUOTE':
        return {
          type: 'blockquote',
          content: Array.from(element.childNodes)
            .map((child) => {
              if (child.nodeType === Node.ELEMENT_NODE) {
                return parseBlockNode(child as Element);
              }
              return {
                type: 'paragraph',
                content: parseInlineNode(child),
              };
            })
            .filter((node) => node.content && node.content.length > 0),
        };

      case 'PRE':
        const codeElement = element.querySelector('code');
        const codeText = codeElement
          ? codeElement.textContent || ''
          : element.textContent || '';
        return {
          type: 'codeBlock',
          content: codeText ? [{ type: 'text', text: codeText }] : [],
        };

      default:
        // Для неизвестных блочных элементов создаем параграф
        return {
          type: 'paragraph',
          content: parseInlineNode(element),
        };
    }
  };

  const nodes: any[] = [];

  Array.from(doc.body.childNodes).forEach((child) => {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const element = child as Element;
      const node = parseBlockNode(element);
      if (node) {
        nodes.push(node);
      }
    } else if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent?.trim();
      if (text) {
        nodes.push({
          type: 'paragraph',
          content: [{ type: 'text', text }],
        });
      }
    }
  });

  return nodes.length > 0 ? nodes : [];
};

// Вспомогательная функция для извлечения текста из node
const extractText = (node: any): string => {
  if (!node.content) return '';

  return node.content
    .map((child: any) => {
      if (child.type === 'text') {
        return child.text || '';
      }
      if (child.content) {
        return extractText(child);
      }
      return '';
    })
    .join('');
};

// Конвертация node в HTML (для text блоков с форматированием)
const nodeToHTML = (node: any, skipWrapper: boolean = false): string => {
  if (!node) return '';

  // Обработка текстовых узлов
  if (node.type === 'text') {
    let text = node.text || '';

    // Экранируем HTML спецсимволы в тексте
    text = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Применяем marks (форматирование) в правильном порядке
    if (node.marks && node.marks.length > 0) {
      const sortedMarks = [...node.marks].reverse();

      sortedMarks.forEach((mark: any) => {
        switch (mark.type) {
          case 'bold':
            text = `<strong>${text}</strong>`;
            break;
          case 'italic':
            text = `<em>${text}</em>`;
            break;
          case 'strike':
            text = `<s>${text}</s>`;
            break;
          case 'code':
            text = `<code>${text}</code>`;
            break;
          case 'link':
            const href = (mark.attrs?.href || '#').replace(/"/g, '&quot;');
            const target = mark.attrs?.target || '_blank';
            text = `<a href="${href}" target="${target}">${text}</a>`;
            break;
          case 'highlight':
            text = `<mark>${text}</mark>`;
            break;
        }
      });
    }

    return text;
  }

  if (node.type === 'hardBreak') {
    return '<br>';
  }

  // Обработка блочных элементов
  const content = node.content
    ? node.content.map((child: any) => nodeToHTML(child, false)).join('')
    : '';

  switch (node.type) {
    case 'paragraph':
      return skipWrapper ? content : `<p>${content}</p>`;

    case 'bulletList':
      return `<ul>${content}</ul>`;

    case 'orderedList':
      return `<ol>${content}</ol>`;

    case 'listItem':
      return `<li>${content}</li>`;

    case 'blockquote':
      return `<blockquote>${content}</blockquote>`;

    case 'codeBlock':
      const language = node.attrs?.language || '';
      return `<pre${language ? ` data-language="${language}"` : ''}><code>${content}</code></pre>`;

    default:
      // Для неизвестных типов просто возвращаем содержимое
      return content;
  }
};

// Преобразование blocks array → Tiptap JSON
export const blocksToTiptap = (blocks: any[]): any => {
  if (!blocks || !Array.isArray(blocks)) {
    return { type: 'doc', content: [] };
  }

  const content: any[] = [];

  blocks.forEach((block) => {
    const {
      type,
      content: textContent,
      url,
      preview_url,
      txt,
      presentation,
      summary,
      timecodes,
      is_blur,
    } = block;

    switch (type) {
      case 'text':
        // Парсим HTML в Tiptap nodes (блочные элементы)
        if (textContent && textContent.trim()) {
          const parsedNodes = htmlToTiptapNodes(textContent);
          // Добавляем все распарсенные блоки
          content.push(...parsedNodes);
        }
        break;

      case 'header1':
        content.push({
          type: 'heading',
          attrs: { level: 1 },
          content: textContent ? [{ type: 'text', text: textContent }] : [],
        });
        break;

      case 'header2':
        content.push({
          type: 'heading',
          attrs: { level: 2 },
          content: textContent ? [{ type: 'text', text: textContent }] : [],
        });
        break;

      case 'header3':
        content.push({
          type: 'heading',
          attrs: { level: 3 },
          content: textContent ? [{ type: 'text', text: textContent }] : [],
        });
        break;

      case 'audio':
        content.push({
          type: 'audioBlock',
          attrs: { url, is_blur },
        });
        break;

      case 'image':
        content.push({
          type: 'imageBlock',
          attrs: { url, is_blur },
        });
        break;

      case 'video':
        content.push({
          type: 'videoBlock',
          attrs: {
            url,
            preview_url,
            txt,
            presentation,
            summary,
            timecodes,
            is_blur,
          },
        });
        break;

      case 'checkbox':
        content.push({
          type: 'checkboxBlock',
          attrs: { content: textContent, is_blur },
        });
        break;

      default:
        // Игнорируем неизвестные типы
        break;
    }
  });

  return {
    type: 'doc',
    content,
  };
};

// Преобразование Tiptap JSON → blocks array
export const tiptapToBlocks = (tiptapJSON: any): any[] => {
  if (!tiptapJSON || !tiptapJSON.content) {
    return [];
  }

  const blocks: any[] = [];

  tiptapJSON.content.forEach((node: any, index: number) => {
    const baseBlock = {
      position: index + 1,
      is_blur: node.attrs?.is_blur !== undefined ? node.attrs.is_blur : true,
    };

    switch (node.type) {
      case 'heading':
        // Только h1, h2, h3 сохраняем как отдельные блоки
        const level = node.attrs?.level || 1;
        const headerText = extractText(node);
        if (headerText && headerText.trim()) {
          blocks.push({
            ...baseBlock,
            type: `header${level}`,
            content: headerText,
          });
        }
        break;

      case 'audioBlock':
        blocks.push({
          ...baseBlock,
          type: 'audio',
          url: node.attrs?.url || '',
        });
        break;

      case 'imageBlock':
        blocks.push({
          ...baseBlock,
          type: 'image',
          url: node.attrs?.url || '',
        });
        break;

      case 'videoBlock':
        blocks.push({
          ...baseBlock,
          type: 'video',
          url: node.attrs?.url || '',
          preview_url: node.attrs?.preview_url || '',
          txt: node.attrs?.txt || '',
          presentation: node.attrs?.presentation || '',
          summary: node.attrs?.summary || '',
          timecodes: node.attrs?.timecodes || [],
        });
        break;

      case 'checkboxBlock':
        blocks.push({
          ...baseBlock,
          type: 'checkbox',
          content: node.attrs?.content || '',
        });
        break;

      case 'paragraph':
      case 'bulletList':
      case 'orderedList':
      case 'blockquote':
      case 'codeBlock':
      default:
        // Все остальные типы (включая списки, цитаты, код) конвертируем в HTML
        const htmlContent = nodeToHTML(node, false);
        if (htmlContent && htmlContent.trim()) {
          blocks.push({
            ...baseBlock,
            type: 'text',
            content: htmlContent,
          });
        }
        break;
    }
  });

  return blocks;
};
